import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Camera, Send, FileText, MessageSquare, ChevronDown, ChevronUp, RefreshCw, Gift } from 'lucide-react';
import complaintService from '../../services/complaintService';
import { formatIstDateTime } from '../../lib/constants';

const complaintSuggestions = [
  { value: 'Product Damaged', label: 'Product Damaged', hint: 'The item arrived broken, leaking, or unusable.' },
  { value: 'Wrong Item Delivered', label: 'Wrong Item Delivered', hint: 'You received a different product than ordered.' },
  { value: 'Missing Item', label: 'Missing Item', hint: 'Part of your order is missing or skipped.' },
  { value: 'Late Delivery', label: 'Late Delivery', hint: 'The order arrived later than expected.' },
  { value: 'Poor Quality', label: 'Poor Quality', hint: 'The product quality does not match the description.' },
  { value: 'Packaging Issue', label: 'Packaging Issue', hint: 'The item was poorly packed or leaked during transit.' },
  { value: 'Other', label: 'Other', hint: 'Describe the issue in your own words.' },
];

const IMAGE_REQUIRED_TYPES = new Set(['Product Damaged', 'Wrong Item Delivered', 'Poor Quality', 'Packaging Issue']);

const statusClass = (status) => {
  if (status === 'resolved') return 'bg-green-100 text-green-700';
  if (status === 'in_review') return 'bg-yellow-100 text-yellow-700';
  if (status === 'closed') return 'bg-gray-100 text-gray-700';
  return 'bg-orange-100 text-orange-700';
};

const ComplaintCard = ({ complaint }) => {
  const hasAdminReply = complaint.replies?.some((r) => r.senderType === 'admin') || !!complaint.adminComment;
  const [expanded, setExpanded] = useState(hasAdminReply);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replies, setReplies] = useState(complaint.replies || []);

  // Sync replies from parent when server data refreshes
  const serverReplyIds = (complaint.replies || []).map((r) => r.id).join(',');
  useEffect(() => {
    setReplies((prev) => {
      const serverReplies = complaint.replies || [];
      const serverIdSet = new Set(serverReplies.map((r) => r.id));
      // Keep any locally-added replies not yet reflected in server data
      const localOnly = prev.filter((r) => !serverIdSet.has(r.id));
      return [...serverReplies, ...localOnly];
    });
  }, [serverReplyIds]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    const result = await complaintService.addUserReply(complaint.id, replyText.trim());
    if (result.success) {
      setReplies((prev) => [...prev, result.data]);
      setReplyText('');
    }
    setSending(false);
  };

  return (
    <div className={`rounded-3xl border p-4 ${hasAdminReply ? 'border-[#548B2E] bg-[#f8fcf3]' : 'border-gray-200'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">{complaint.issueType}</p>
          <p className="text-xs text-gray-500">
            Submitted on {formatIstDateTime(complaint.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasAdminReply && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#2e4e18] text-white text-[11px] font-semibold px-3 py-1">
              <MessageSquare className="w-3 h-3" /> Admin replied
            </span>
          )}
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusClass(complaint.status)}`}>
            {complaint.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      <div className="mt-3 text-sm text-gray-600">
        <p>{complaint.description}</p>
        {complaint.orderReference && (
          <p className="mt-2 text-xs text-gray-500">Order: {complaint.orderReference}</p>
        )}
      </div>

      {complaint.images?.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          {complaint.images.map((src) => (
            <img key={src} src={src} alt="complaint" className="h-28 w-full object-cover rounded-2xl border border-gray-200" />
          ))}
        </div>
      )}

      {/* Thread toggle */}
      {(() => {
        let toggleLabel = 'Add a reply';
        if (expanded) toggleLabel = 'Hide conversation';
        else if (hasAdminReply) toggleLabel = `View admin reply & respond (${replies.length})`;
        const ChevronIcon = expanded ? ChevronUp : ChevronDown;
        return (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="mt-4 flex items-center gap-2 text-xs text-[#548B2E] font-medium hover:underline"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {toggleLabel}
            <ChevronIcon className="w-3.5 h-3.5" />
          </button>
        );
      })()}

      {expanded && (
        <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-3 space-y-3">
          {/* Legacy adminComment shown only when no structured replies */}
          {complaint.adminComment && replies.length === 0 && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-[#2e4e18] text-white">
                <p className="text-[10px] font-semibold mb-1 text-green-200">Admin Team</p>
                <p className="text-sm">{complaint.adminComment}</p>
              </div>
            </div>
          )}

          {/* Structured reply thread */}
          {replies.map((reply) => {
            if (reply.senderType === 'system') {
              return (
                <div key={reply.id} className="flex justify-center my-2">
                  <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl shadow-sm text-xs text-amber-900 max-w-[90%]">
                    <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm font-bold text-sm">
                      ₹
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="font-semibold text-amber-900">{reply.message}</span>
                      <span className="text-[10px] text-amber-600 font-medium">
                        System Transaction · {new Date(reply.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
            const isAdmin = reply.senderType === 'admin';
            const bubbleClass = isAdmin
              ? 'bg-[#2e4e18] text-white'
              : 'bg-[#f2f8eb] border border-[#c8e6a0] text-gray-800';
            const nameClass = isAdmin ? 'text-green-200' : 'text-[#2e4e18]';
            const timeClass = isAdmin ? 'text-green-300' : 'text-gray-400';
            return (
              <div key={reply.id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${bubbleClass}`}>
                  <p className={`text-[10px] font-semibold mb-1 ${nameClass}`}>{reply.senderName}</p>
                  <p className="text-sm">{reply.message}</p>
                  <p className={`text-[10px] mt-1 ${timeClass}`}>
                    {new Date(reply.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
                  </p>
                </div>
              </div>
            );
          })}

          {replies.length === 0 && !complaint.adminComment && (
            <p className="text-center text-xs text-gray-400 py-3">No replies yet — admin will respond here soon.</p>
          )}

          {/* Reply input */}
          <div className="flex gap-2 pt-1">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
              placeholder="Reply to this complaint..."
              className="flex-1 border border-gray-200 rounded-2xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white"
            />
            <button
              onClick={handleSendReply}
              disabled={sending || !replyText.trim()}
              className="bg-[#2e4e18] text-white px-3 py-2 rounded-2xl hover:bg-[#3f6b20] transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

ComplaintCard.propTypes = {
  complaint: PropTypes.shape({
    id: PropTypes.number.isRequired,
    issueType: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    orderReference: PropTypes.string,
    adminComment: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    replies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        senderType: PropTypes.string.isRequired,
        senderName: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};

const ComplaintsSection = () => {
  const [searchParams] = useSearchParams();
  const [issueType, setIssueType] = useState('Product Damaged');
  const [description, setDescription] = useState('');
  const [orderReference, setOrderReference] = useState(() => searchParams.get('orderReference') || '');
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [message, setMessage] = useState(null);
  const fetchRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const refParam = searchParams.get('orderReference');
    if (refParam) {
      setOrderReference(refParam);
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [searchParams]);

  const fetchComplaints = async (page = 1, silent = false) => {
    if (!silent) setLoading(true);
    const result = await complaintService.getMyComplaints({ page, limit: 10 });
    if (result.success) {
      setComplaints(result.data.complaints || []);
      setPagination(result.data.pagination || pagination);
    }
    if (!silent) setLoading(false);
  };

  // Keep ref current so the interval always calls the latest version
  fetchRef.current = fetchComplaints;

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchRef.current(1, true);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchRef.current();
    const apiUrl = process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api';
    const es = new EventSource(`${apiUrl}/complaints/stream`);
    es.addEventListener('update', () => fetchRef.current(1, true));
    return () => es.close();
  }, []);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = selectedFiles.filter((file) => file.type.startsWith('image/'));
    setImages(validFiles.slice(0, 4));
    setPreviews(validFiles.slice(0, 4).map((file) => URL.createObjectURL(file)));
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setMessage('Please describe your complaint so our team can help.');
      return;
    }
    if (IMAGE_REQUIRED_TYPES.has(issueType) && images.length === 0) {
      setMessage('Please attach at least one image — it is required for this type of complaint.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    const formData = new FormData();
    formData.append('issueType', issueType);
    formData.append('description', description.trim());
    if (orderReference.trim()) {
      formData.append('orderReference', orderReference.trim());
    }
    images.forEach((file) => formData.append('images', file));

    const result = await complaintService.submitComplaint(formData);
    if (result.success) {
      setDescription('');
      setOrderReference('');
      setImages([]);
      setPreviews([]);
      setMessage('Complaint submitted successfully. Our admin team will review it shortly.');
      fetchComplaints(1);
    } else {
      setMessage(result.error || 'Unable to submit complaint. Please try again.');
    }

    setSubmitting(false);
  };

  const currentHint = complaintSuggestions.find((item) => item.value === issueType)?.hint;

  const renderComplaints = () => {
    if (loading) return <div className="text-center py-12 text-gray-500">Loading your complaints…</div>;
    if (complaints.length === 0) return <div className="text-center py-12 text-gray-500">No complaints submitted yet.</div>;
    return (
      <div className="space-y-4">
        {complaints.map((complaint) => (
          <ComplaintCard key={complaint.id} complaint={complaint} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div ref={formRef} className="bg-white rounded-3xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-3xl bg-[#F8F4E8] flex items-center justify-center text-[#548B2E]">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Submit a Complaint</h2>
            <p className="text-sm text-gray-600">Tell us what went wrong and attach images if needed.</p>
          </div>
        </div>

        {/* App matched yellowish policy notice */}
        <div className="mb-5 p-4 rounded-2xl bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] text-xs leading-relaxed flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 flex-shrink-0" />
          <span>
            <strong>Policy Info:</strong> As per company policy, any product quality issues or damaged items must be reported within 24 hours after delivery.
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="issueType" className="block text-sm font-medium text-gray-700 mb-2">Complaint Type</label>
            <select
              id="issueType"
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            >
              {complaintSuggestions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {currentHint && <p className="mt-2 text-xs text-gray-500">{currentHint}</p>}
          </div>

          <div>
            <label htmlFor="orderReference" className="block text-sm font-medium text-gray-700 mb-2">Order Reference (optional)</label>
            <input
              id="orderReference"
              type="text"
              value={orderReference}
              onChange={(e) => setOrderReference(e.target.value)}
              placeholder="Order #12345 or delivery ID"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            />
          </div>
        </div>

        <div className="mt-4">
          <label htmlFor="complaintDescription" className="block text-sm font-medium text-gray-700 mb-2">Complaint Details</label>
          <textarea
            id="complaintDescription"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Write the issue clearly so we can resolve it faster."
            className="w-full border border-gray-300 rounded-3xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
          />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm font-medium text-gray-700">
              Upload Images
              {IMAGE_REQUIRED_TYPES.has(issueType)
                ? <span className="ml-1 text-red-500">*</span>
                : <span className="ml-1 text-gray-400 font-normal">(optional)</span>}
            </p>
            <span className="text-xs text-gray-500">Up to 4 images</span>
          </div>
          {IMAGE_REQUIRED_TYPES.has(issueType) && (
            <p className="text-xs text-red-500 mb-2">At least 1 image is required for this complaint type.</p>
          )}
          <label
            htmlFor="complaintImages"
            className={`cursor-pointer inline-flex items-center gap-2 rounded-3xl border border-dashed px-4 py-4 w-full text-sm transition-colors ${
              IMAGE_REQUIRED_TYPES.has(issueType) && images.length === 0
                ? 'border-red-300 text-red-400 hover:border-red-400'
                : 'border-gray-300 text-gray-600 hover:border-[#548B2E] hover:text-[#548B2E]'
            }`}
          >
            <Camera className="w-4 h-4" />
            <span>{images.length ? `${images.length} selected` : 'Choose images'}</span>
            <input id="complaintImages" type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
          </label>
          {previews.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {previews.map((src) => (
                <img key={src} src={src} alt="preview" className="h-28 w-full object-cover rounded-2xl border border-gray-200" />
              ))}
            </div>
          )}
        </div>

        {message && (
          <div className="mt-4 rounded-2xl bg-[#F1F8E9] border border-[#deeeca] px-4 py-3 text-sm text-gray-800">
            {message}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-3xl bg-[#2e4e18] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#3f6b20] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting...' : 'Send Complaint'}
          <Send className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-3xl bg-[#f2f8eb] flex items-center justify-center text-[#3f6b20]">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Your Complaints</h2>
              <p className="text-sm text-gray-600">Track status and reply to your complaints.</p>
            </div>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            title="Refresh complaints"
            className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {renderComplaints()}
      </div>
    </div>
  );
};

export default ComplaintsSection;
